import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useMobileOrientation, useDeviceSelectors } from 'react-device-detect';
import DeviceNotice from './components/DeviceNotice';
import Home from './pages/Home';
import ClaimSuccess from './pages/ClaimSuccess';
import ClaimFailed from './pages/ClaimFailed';
import CodeNotWorking from './pages/CodeNotWorking';
import CampaignEnded from './pages/CampaignEnded';
import PreCampaign from './pages/PreCampaign';
import { trackPageView } from './analytics';

const StyledApp = styled.div`
  position: relative;
  margin: 0 auto;
  padding: 58px 16px 0;
  background: #ffffff;
  box-sizing: border-box;
  min-height: 100vh;
  transition: opacity 0.25s;
  overflow: hidden;
`;

// In development requests go through the dev-server proxy (src/setupProxy.js), which
// forwards to REACT_APP_API_BASE and adds the Referer header the API requires.
const API_BASE = process.env.NODE_ENV === 'development' ? '/api' : process.env.REACT_APP_API_BASE;
const API_KEY = process.env.REACT_APP_API_KEY;

// Make sure users won't be redirected to other pages during claim process
const CLAIM_SCREENS = ['claim-success', 'claim-failed', 'code-not-working'];

async function checkPromoCode() {
  const response = await fetch(`${API_BASE}/code`, {
    method: 'POST',
    headers: {
      'x-api-key': API_KEY,
    },
  });

  if (!response.ok) {
    return { available: false };
  }

  const data = await response.json();

  if (data?.code) {
    return { available: true, prizeCode: data.code };
  }

  return { available: false };
}

function App() {
  const [page, setCurrentPage] = useState('home');
  const [transitioning, setTransitioning] = useState(false);
  const [prizeCode, setPrizeCode] = useState('');
  const [offer, setOffer] = useState(null);
  const [statusChecked, setStatusChecked] = useState(false);
  const [selectors] = useDeviceSelectors(window.navigator.userAgent);
  const { isMobileOnly } = selectors;
  const { isPortrait } = useMobileOrientation();

  const pageRef = useRef(page);
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    const allImages = require.context('./assets/img/', false, /\.(png|svg)$/);
    allImages.keys().forEach((item) => {
      const importedImg = allImages(item);
      const img = new Image();
      img.src = importedImg;
    });

    const savedCode = localStorage.getItem('redeemedCode');
    if (savedCode) {
      setPrizeCode(savedCode);
      setCurrentPage('claim-success');
    }
  }, []);

  // Call campaign status api every 2 minutes to check if the campaign is ended
  useEffect(() => {
    const checkCampaignStatus = async () => {
      try {
        const response = await fetch(`${API_BASE}/status`, {
          method: 'GET',
          headers: {
            'x-api-key': API_KEY,
          },
        });

        if (!response.ok) {
          console.error('Campaign status request failed:', response.status);
          setCurrentPage('campaign-ended');
          return;
        }

        const data = await response.json();
        const state = data.state;

        // Prevent same states causing unnecessary reassigning due to polling every 2 minutes
        setOffer((prev) => {
            const keys = ['venue', 'endDate', 'matchEndDate', 'timezone', 'totalCodeCount'];
            const hasChanged = keys.some((key) => prev?.[key] !== data[key]);
            return hasChanged ? Object.fromEntries(keys.map((k) => [k, data[k]])) : prev;
          }
        );

        if (state === 'PRE_CAMPAIGN') {
          setCurrentPage('pre-campaign');
        } else if (state === 'POST_CAMPAIGN') {
          setCurrentPage('campaign-ended');
        } else if (state === 'LIVE' && !CLAIM_SCREENS.includes(pageRef.current)) {
          setCurrentPage('home');
        }
      } catch (error) {
        console.error('Error checking campaign status:', error);
      } finally {
        // Releases the first page view whether the call succeeded, errored or 404'd —
        // otherwise a failing status endpoint would suppress analytics entirely.
        setStatusChecked(true);
      }
    };

    checkCampaignStatus();
    const interval = setInterval(checkCampaignStatus, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const screen = !isMobileOnly ? 'device-notice' : !isPortrait ? 'rotate-device' : page;

  useEffect(() => {
    if (statusChecked) trackPageView(screen);
  }, [screen, statusChecked]);

  const setPage = (page) => {
    setTransitioning(true);
    setTimeout(() => {
      setCurrentPage(page);
      setTransitioning(false);
    }, 100);
  };

  const handleClaimNow = async () => {
    const result = await checkPromoCode();

    if (result.available) {
      localStorage.setItem('redeemedCode', result.prizeCode);
      setPrizeCode(result.prizeCode);
      setPage('claim-success');
    } else {
      setPage('claim-failed');
    }
  };

  if (!isMobileOnly) return <DeviceNotice offer={offer} type="view-on-mobile" />;

  if (!isPortrait) return <DeviceNotice offer={offer} type="rotate-device" />;

  return (
    <StyledApp style={{ opacity: transitioning ? 0 : 1 }}>
      {page === 'pre-campaign' && <PreCampaign setPage={setPage} offer={offer} />}
      {page === 'home' && <Home setPage={setPage} onClaimNow={handleClaimNow} offer={offer} />}
      {page === 'claim-success' && <ClaimSuccess prizeCode={prizeCode} setPage={setPage} offer={offer} />}
      {page === 'claim-failed' && <ClaimFailed offer={offer} />}
      {page === 'code-not-working' && <CodeNotWorking setPage={setPage} prizeCode={prizeCode} />}
      {page === 'campaign-ended' && <CampaignEnded />}
    </StyledApp>
  );
}

export default App;
